import { allowed, readConsent, visitorIsRegulated } from "@/lib/consent";
import { funnelOptedOut } from "@/lib/privacySignals";
import { isInternalTraffic } from "@/lib/internalTraffic";
import { tierForTool } from "@shared/tiers";
import { CHECKOUT_PLAN_VALUES, type CheckoutPlan, type Tier } from "@shared/purchaseEvent";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/** False when the visitor opted out: do-not-track / Global Privacy Control, or (in covered regions) no analytics consent. */
export function analyticsPermitted(): boolean {
  try {
    if (funnelOptedOut()) return false;
    return allowed("analytics", visitorIsRegulated(), readConsent());
  } catch { return false; }
}

/**
 * Fires a GA4 event via the gtag.js snippet already loaded in index.html.
 * Safe to call even if gtag isn't available (ad blockers, consent declined,
 * or the script hasn't loaded yet) -- silently does nothing rather than
 * throwing.
 */
export function trackEvent(eventName: string, params?: Record<string, unknown>) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  if (!analyticsPermitted()) return;
  // Our own devices and automated browsers are labelled so GA4's internal-traffic filter can drop them.
  window.gtag("event", eventName, isInternalTraffic() ? { ...params, traffic_type: "internal" } : params);
}

/** Only a plain tool slug is ever sent as a tool id. */
export function safeToolSlug(toolId: string | undefined | null): string {
  return toolId && /^[a-z0-9][a-z0-9-]{0,63}$/.test(toolId) ? toolId : "unknown";
}


/**
 * Fires when a tool actually finishes doing its job -- a file merged, a
 * conversion completed, a book assembled -- not when a button is clicked.
 * This is deliberately ONE event name (`tool_completed`) with a `tool_id`
 * parameter, rather than a different event name per tool: GA4's own UI
 * becomes unusable with 200+ distinct event names, and marking ONE event
 * as a key event, then breaking it down by `tool_id` in Explore, gives
 * the same visibility with none of that mess.
 *
 * Before this existed, GA4 had no way to see whether the site's actual
 * product -- successful tool use -- was working. The only events being
 * tracked were generic (page_view, scroll, login), and login specifically
 * was structurally rare after the login-free architecture change, which
 * is why key events were clustering almost entirely on the homepage.
 */
export function trackToolCompleted(toolId: string, category: string) {
  emitCompleted(toolId, category, "tool");
}

/**
 * The shared mechanism: called by lib/usageGate whenever a tool visit spends a trial use (a download, a copy,
 * print or save, a Check / Run button). That is the same moment the daily count moves, so every tool reports one
 * `tool_completed` per spent use with no per-tool code. Tools that already report their own completion with
 * trackToolCompleted are matched against it (see emitCompleted), never counted twice.
 */
export function trackToolUseSpent(toolId: string, category: string) {
  emitCompleted(toolId, category, "gate");
}

// One run, one event, whichever side reports first. A tool's own report (result ready) and the usage gate's report
// (use spent) can both describe the same run, in either order and a few seconds to minutes apart, so the second
// one of a different origin consumes the first instead of sending again. Same origin again is a new run, apart
// from the short repeated-callback window.
const TOOL_COMPLETED_DEDUPE_MS = 2500;
const MATCH_WINDOW_MS = 10 * 60_000;
const lastCompleted = new Map<string, number>();
const unmatched = new Map<string, { src: "tool" | "gate"; at: number }>();
function emitCompleted(toolId: string, category: string, src: "tool" | "gate") {
  const slug = safeToolSlug(toolId);
  const now = Date.now();
  const last = lastCompleted.get(slug);
  if (last !== undefined && now - last < TOOL_COMPLETED_DEDUPE_MS) return;
  lastCompleted.set(slug, now);
  const u = unmatched.get(slug);
  if (u && u.src !== src && now - u.at < MATCH_WINDOW_MS) { unmatched.delete(slug); return; }
  unmatched.set(slug, { src, at: now });
  trackEvent("tool_completed", { tool_id: slug, tool_category: category, tier: tierForTool(slug) });
}
export function __resetAnalyticsForTests() { lastCompleted.clear(); unmatched.clear(); lastCheckout.clear(); }

const lastCheckout = new Map<string, number>();

/**
 * Fires `checkout_start` when a Stripe Checkout link has been created and the visitor is about to be sent to it.
 * Params: tier, plan, value, currency (and tool_id when it started from a tool's paywall). Same value as the
 * price the plan sells at; the real, server-confirmed amount arrives later on `purchase`.
 */
export function trackCheckoutStart(plan: CheckoutPlan, tier: Tier | "api", toolId?: string) {
  const key = `${tier}:${plan}`;
  const now = Date.now();
  const last = lastCheckout.get(key);
  if (last !== undefined && now - last < 3000) return;
  lastCheckout.set(key, now);
  const value = tier === "api" ? undefined : CHECKOUT_PLAN_VALUES[tier][plan as "day_pass" | "monthly"];
  trackEvent("checkout_start", {
    tier, plan, currency: "USD",
    ...(value !== undefined ? { value } : {}),
    ...(toolId ? { tool_id: safeToolSlug(toolId) } : {}),
  });
}

/** What may be sent to GA as a search term: trimmed, lower-cased, capped, and never anything that looks like an email address. */
export function cleanSearchTerm(raw: string): string | null {
  const t = raw.trim().toLowerCase().replace(/\s+/g, " ").slice(0, 100);
  if (t.length < 2 || t.includes("@")) return null;
  return t;
}

/**
 * Fires the GA4 recommended `search` event (param `search_term`) when a visitor submits a site search, plus
 * `search_no_results` when nothing matched, so missing tools show up in GA next to the internal search log.
 * Called once per submitted search (never while typing). Needs `search_term` registered as an event-scoped custom
 * dimension in GA4 before it appears in reports.
 */
export function trackSiteSearch(rawTerm: string, noResults: boolean) {
  const term = cleanSearchTerm(rawTerm);
  if (!term) return;
  trackEvent("search", { search_term: term });
  if (noResults) trackEvent("search_no_results", { search_term: term });
}
